/**
 * DoctorDocumentsManager
 * Full document management UI for doctors:
 * - Lists uploaded documents with status
 * - View via Signed URL (DocumentViewerModal)
 * - Upload new documents
 * - Replace existing documents
 * - Shows rejection reason
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import {
  FileText,
  Upload,
  Eye,
  RefreshCw,
  CheckCircle2,
  Clock,
  XCircle,
  AlertTriangle,
  Loader2,
  Plus,
  ChevronDown,
} from 'lucide-react';
import { DocumentViewerModal } from './DocumentViewerModal';
import {
  ConfirmationVideoRecorder,
  ConfirmationVideoSourceToggle,
  type ConfirmationVideoSourceMode,
} from './ConfirmationVideoRecorder';
import {
  getDoctorDocuments,
  getDoctorDocumentViewUrl,
  unlockDoctorDocumentView,
  uploadDoctorDocument,
  replaceDoctorDocument,
  type DoctorDocumentDTO,
} from '@/services/doctors';
import { validateConfirmationVideoFile } from '@/lib/confirmationVideo';
import { isCameraRecordingSupported } from '@/lib/confirmationVideoRecorder';

interface DoctorDocumentsManagerProps {
  doctorId: string;
}

const DOC_TYPES = [
  { value: 'CRM', label: 'CRM' },
  { value: 'SPECIALTY_CERTIFICATE', label: 'Certificado de Especialidade' },
  { value: 'INSCRIPTION_PROOF', label: 'Comprovante de Inscrição' },
  { value: 'CONFIRMATION_VIDEO', label: 'Vídeo de confirmação (3s)' },
  { value: 'DIGITAL_CERTIFICATE', label: 'Certificado .pfx (opcional — não assina na Memed)' },
] as const;

type DocTypeValue = (typeof DOC_TYPES)[number]['value'];
type CertificateKind = 'A1' | 'A2' | 'A3' | 'A4';

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'APPROVED':
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Aprovado
        </span>
      );
    case 'REJECTED':
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400">
          <XCircle className="w-3.5 h-3.5" />
          Recusado
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
          <Clock className="w-3.5 h-3.5" />
          Em análise
        </span>
      );
  }
}

function formatFileSize(size?: number | null): string {
  if (size === undefined || size === null || Number.isNaN(Number(size))) {
    return 'Tamanho indisponível';
  }
  const bytes = Number(size);
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function DoctorDocumentsManager({ doctorId }: DoctorDocumentsManagerProps) {
  const [documents, setDocuments] = useState<DoctorDocumentDTO[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Viewer
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewingDoc, setViewingDoc] = useState<DoctorDocumentDTO | null>(null);

  // Upload new
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  // Tipo selecionado para novo upload
  const [uploadType, setUploadType] = useState<DocTypeValue>('CRM');
  const [certificateKind, setCertificateKind] = useState<CertificateKind>('A1');
  const [certificatePassword, setCertificatePassword] = useState('');
  const newFileInputRef = useRef<HTMLInputElement>(null);
  const [videoSourceMode, setVideoSourceMode] = useState<ConfirmationVideoSourceMode>('upload');
  const recordingSupported = isCameraRecordingSupported();

  // Replace existing
  const [replacingDocId, setReplacingDocId] = useState<string | null>(null);
  const [isReplacing, setIsReplacing] = useState(false);
  const replaceFileInputRef = useRef<HTMLInputElement>(null);

  // Tipos já enviados (variáveis derivadas — calculadas após todos os hooks)
  const uploadedTypes = documents.map((d) => d.documentType || d.type);
  const availableTypes = DOC_TYPES.filter((t) => !uploadedTypes.includes(t.value));
  const MAX_DOCS = DOC_TYPES.length;
  const isAtLimit = documents.length >= MAX_DOCS;

  const loadDocuments = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getDoctorDocuments(doctorId);
      if (response.success && response.data) {
        setDocuments(response.data);
      } else {
        setError(response.error || 'Erro ao carregar documentos');
      }
    } catch {
      setError('Erro de conexão');
    } finally {
      setIsLoading(false);
    }
  }, [doctorId]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // ── Upload new document ──────────────────────────────────────────────────

  const handleUpload = async () => {
    if (!uploadFile || !uploadType) return;
    setIsUploading(true);
    setUploadError(null);

    if (uploadType === 'CONFIRMATION_VIDEO') {
      const videoValidation = await validateConfirmationVideoFile(uploadFile);
      if (!videoValidation.valid) {
        setUploadError(videoValidation.error);
        setIsUploading(false);
        return;
      }
    }

    if (uploadType === 'DIGITAL_CERTIFICATE') {
      const isPkcs12 = /\.(pfx|p12)$/i.test(uploadFile.name);
      if ((certificateKind === 'A1' || isPkcs12) && !certificatePassword.trim()) {
        setUploadError('Informe a senha do certificado digital.');
        setIsUploading(false);
        return;
      }
      if (!/\.(pfx|p12|cer|crt|pem)$/i.test(uploadFile.name)) {
        setUploadError('Formato inválido. Use .pfx, .p12, .cer, .crt ou .pem.');
        setIsUploading(false);
        return;
      }
    }

    const response = await uploadDoctorDocument(
      doctorId,
      uploadFile,
      uploadType,
      uploadType === 'DIGITAL_CERTIFICATE'
        ? {
            certificateKind,
            certificatePassword: certificatePassword.trim() || undefined,
          }
        : undefined,
    );

    if (response.success) {
      setShowUploadForm(false);
      setUploadFile(null);
      setCertificatePassword('');
      await loadDocuments();
    } else {
      setUploadError(response.error || 'Erro ao enviar documento');
    }
    setIsUploading(false);
  };

  // ── Replace document ─────────────────────────────────────────────────────

  const handleReplaceFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !replacingDocId) return;

    const replacingDoc = documents.find((d) => d.id === replacingDocId);
    const replacingType = replacingDoc?.documentType || replacingDoc?.type;

    if (replacingType === 'CONFIRMATION_VIDEO') {
      const videoValidation = await validateConfirmationVideoFile(file);
      if (!videoValidation.valid) {
        alert(videoValidation.error);
        e.target.value = '';
        setReplacingDocId(null);
        return;
      }
    }

    if (replacingType === 'DIGITAL_CERTIFICATE') {
      const pwd = window.prompt(
        'Informe a senha atual do certificado para substituir o arquivo:',
      );
      if (pwd === null) {
        e.target.value = '';
        setReplacingDocId(null);
        return;
      }
      if (!pwd.trim() && replacingDoc?.hasCertificatePassword) {
        alert('Senha do certificado obrigatória para substituir.');
        e.target.value = '';
        setReplacingDocId(null);
        return;
      }
      setIsReplacing(true);
      const response = await replaceDoctorDocument(doctorId, replacingDocId, file, {
        currentPassword: pwd,
        certificatePassword: pwd,
        certificateKind: (replacingDoc?.certificateKind as CertificateKind) || 'A1',
      });
      if (response.success) {
        await loadDocuments();
      } else {
        alert(response.error || 'Erro ao substituir documento');
      }
      setIsReplacing(false);
      setReplacingDocId(null);
      e.target.value = '';
      return;
    }

    setIsReplacing(true);
    const response = await replaceDoctorDocument(doctorId, replacingDocId, file);

    if (response.success) {
      await loadDocuments();
    } else {
      alert(response.error || 'Erro ao substituir documento');
    }

    setIsReplacing(false);
    setReplacingDocId(null);
    e.target.value = '';
  };

  const fetchViewUrl = useCallback(
    async (userId: string, documentId: string, options?: { password?: string }) => {
      if (options?.password) {
        return unlockDoctorDocumentView(userId, documentId, options.password);
      }
      return getDoctorDocumentViewUrl(userId, documentId);
    },
    [],
  );

  const docTypeLabel = (type: string) =>
    DOC_TYPES.find((d) => d.value === type)?.label ?? type;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-teal-600 animate-spin mr-2" />
        <span className="text-gray-600 dark:text-gray-400">Carregando documentos...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Meus Documentos Profissionais
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            CRM, especialidade, inscrição e vídeo. Assinatura de receita é na Memed (Bird ID), não
            um .pfx neste cadastro.
          </p>
        </div>
        {/* Botão desabilitado quando limite atingido ou não há tipos disponíveis */}
        {!isAtLimit && (
          <button
            onClick={() => {
              // Ao abrir o form, pré-seleciona o primeiro tipo disponível
              if (availableTypes.length > 0) {
                setUploadType(availableTypes[0].value as DocTypeValue);
              }
              setVideoSourceMode('upload');
              setShowUploadForm(!showUploadForm);
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            Novo Documento
          </button>
        )}
      </div>

      {/* Global error */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Mensagem de limite atingido */}
      {isAtLimit && documents.length > 0 && (
        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800 dark:text-amber-200">
            Todos os documentos permitidos já foram enviados. Use &ldquo;Substituir&rdquo; para atualizar algum documento.
          </p>
        </div>
      )}

      {/* Upload form */}
      {showUploadForm && !isAtLimit && (
        <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-xl p-6 space-y-4">
          <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Upload className="w-4 h-4 text-teal-600" />
            Enviar novo documento
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Document type select — apenas tipos não enviados */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Tipo de documento *
              </label>
              <div className="relative">
                <select
                  value={uploadType}
                  onChange={(e) => {
                    const next = e.target.value as DocTypeValue;
                    setUploadType(next);
                    setUploadFile(null);
                    setUploadError(null);
                    setVideoSourceMode('upload');
                  }}
                  className="w-full appearance-none px-3 py-2.5 pr-9 border border-gray-300 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  {availableTypes.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* File input / recorder */}
            <div className="md:col-span-2 space-y-3">
              {uploadType === 'CONFIRMATION_VIDEO' ? (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Vídeo de confirmação (~3s, máx. 20MB) *
                    </label>
                    <ConfirmationVideoSourceToggle
                      mode={videoSourceMode}
                      onChange={(mode) => {
                        setVideoSourceMode(mode);
                        setUploadError(null);
                        if (mode === 'record') setUploadFile(null);
                      }}
                      recordingSupported={recordingSupported}
                      disabled={isUploading}
                    />
                  </div>
                  {!recordingSupported && (
                    <p className="text-xs text-amber-700 dark:text-amber-400">
                      Gravação pela câmera indisponível neste navegador — use o upload.
                    </p>
                  )}
                  {videoSourceMode === 'upload' ? (
                    <input
                      ref={newFileInputRef}
                      type="file"
                      accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
                      onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-sm text-gray-900 dark:text-white file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:bg-teal-50 file:text-teal-700 dark:file:bg-teal-900/20 dark:file:text-teal-400"
                    />
                  ) : (
                    <ConfirmationVideoRecorder
                      disabled={isUploading}
                      onCancel={() => setVideoSourceMode('upload')}
                      onFileReady={(file) => {
                        setUploadFile(file);
                        setUploadError(null);
                      }}
                    />
                  )}
                </>
              ) : uploadType === 'DIGITAL_CERTIFICATE' ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Tipo ICP-Brasil *
                      </label>
                      <select
                        value={certificateKind}
                        onChange={(e) => setCertificateKind(e.target.value as CertificateKind)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-sm"
                      >
                        <option value="A1">A1</option>
                        <option value="A2">A2</option>
                        <option value="A3">A3</option>
                        <option value="A4">A4</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Senha do certificado {certificateKind === 'A1' ? '*' : '(se houver)'}
                      </label>
                      <input
                        type="password"
                        value={certificatePassword}
                        onChange={(e) => setCertificatePassword(e.target.value)}
                        autoComplete="off"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-sm"
                        placeholder="Senha do .pfx / certificado"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Arquivo (.pfx, .p12, .cer, .crt, .pem) *
                    </label>
                    <input
                      ref={newFileInputRef}
                      type="file"
                      accept=".pfx,.p12,.cer,.crt,.pem"
                      onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-sm text-gray-900 dark:text-white file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:bg-teal-50 file:text-teal-700"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Arquivo (PDF, JPG ou PNG) *
                  </label>
                  <input
                    ref={newFileInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-sm text-gray-900 dark:text-white file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:bg-teal-50 file:text-teal-700 dark:file:bg-teal-900/20 dark:file:text-teal-400"
                  />
                </div>
              )}
            </div>
          </div>

          {uploadError && (
            <p className="text-sm text-red-600 dark:text-red-400">{uploadError}</p>
          )}

          {uploadFile && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Arquivo selecionado: {uploadFile.name} ({formatFileSize(uploadFile.size)})
            </p>
          )}

          <div className="flex gap-3 justify-end">
            <button
              onClick={() => {
                setShowUploadForm(false);
                setUploadFile(null);
                setUploadError(null);
                setVideoSourceMode('upload');
              }}
              className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleUpload}
              disabled={!uploadFile || isUploading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isUploading ? (
                <><Loader2 className="w-4 h-4 animate-spin" />Enviando...</>
              ) : (
                <><Upload className="w-4 h-4" />Enviar documento</>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Hidden input for file replace */}
      <input
        ref={replaceFileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.mp4,.webm,.mov,.pfx,.p12,.cer,.crt,.pem,video/mp4,video/webm,video/quicktime"
        className="hidden"
        onChange={handleReplaceFileChange}
      />

      {/* Documents list */}
      {documents.length === 0 ? (
        <div className="bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-800 rounded-xl p-12 text-center">
          <div className="flex justify-center mb-4">
            <div className="p-4 bg-gray-100 dark:bg-neutral-800 rounded-full">
              <FileText className="w-8 h-8 text-gray-400 dark:text-neutral-500" />
            </div>
          </div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
            Nenhum documento enviado
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Envie seu CRM e demais documentos para iniciar o processo de aprovação.
          </p>
          <button
            onClick={() => setShowUploadForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 transition-colors"
          >
            <Upload className="w-4 h-4" />
            Enviar primeiro documento
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {documents.map((doc) => {
            const isLegacy =
              doc.storageProvider !== 'CLOUDFLARE_R2' ||
              !doc.hasFile ||
              ((doc as any).fileUrl && (doc as any).fileUrl.includes('/opt/render/project/source'));

            return (
              <div
                key={doc.id}
                className="bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-800 rounded-xl p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between gap-4">
                  {/* Left: icon + info */}
                  <div className="flex items-start gap-4 flex-1 min-w-0">
                    <div className="p-2.5 bg-teal-50 dark:bg-teal-900/20 rounded-lg flex-shrink-0">
                      <FileText className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {docTypeLabel(doc.documentType || '')}
                        </span>
                        <StatusBadge status={doc.status} />
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {doc.originalFileName || 'Sem nome correto'}
                        {doc.certificateKind ? ` • ${doc.certificateKind}` : ''}
                      </p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-400 dark:text-gray-500">
                        <span>{formatFileSize(doc.fileSize)}</span>
                        <span>•</span>
                        <span>Enviado em {new Date(doc.createdAt).toLocaleDateString('pt-BR')}</span>
                        {isLegacy && (
                          <>
                            <span>•</span>
                            <span className="text-amber-600 dark:text-amber-400 font-medium">
                              ⚠ Legado — reenvio necessário
                            </span>
                          </>
                        )}
                      </div>

                      {/* Rejection reason */}
                      {doc.status === 'REJECTED' && doc.rejectionReason && (
                        <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                          <p className="text-xs font-semibold text-red-700 dark:text-red-400 mb-0.5">
                            Motivo da recusa:
                          </p>
                          <p className="text-xs text-red-600 dark:text-red-300">
                            {doc.rejectionReason}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right: actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {doc.hasFile && !isLegacy && (
                      <button
                        onClick={() => { setViewingDoc(doc); setViewerOpen(true); }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-200 dark:border-neutral-700 rounded-lg hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors text-gray-700 dark:text-gray-300"
                      >
                        <Eye className="w-4 h-4" />
                        Abrir
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setReplacingDocId(doc.id);
                        replaceFileInputRef.current?.click();
                      }}
                      disabled={isReplacing && replacingDocId === doc.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 dark:bg-neutral-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-neutral-700 disabled:opacity-50 transition-colors"
                    >
                      {isReplacing && replacingDocId === doc.id ? (
                        <><Loader2 className="w-4 h-4 animate-spin" />Enviando...</>
                      ) : (
                        <><RefreshCw className="w-4 h-4" />Substituir</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Info box */}
      {documents.length > 0 && (
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-xs text-blue-700 dark:text-blue-400">
            <strong>Armazenamento seguro:</strong> Seus documentos são armazenados de forma privada no
            Cloudflare R2. Os links de visualização expiram em 15 minutos por segurança.
          </p>
        </div>
      )}

      {/* Document Viewer Modal */}
      <DocumentViewerModal
        isOpen={viewerOpen}
        onClose={() => { setViewerOpen(false); setViewingDoc(null); }}
        document={viewingDoc}
        userId={doctorId}
        fetchViewUrl={fetchViewUrl}
      />
    </div>
  );
}
